import { BASKET_TYPES, selectBasket, selectBasketTerms, updateTerms } from 'containers/OrderDetails/basketSlice'
import ConsentPreviewPopup from 'components/popups/ConsentPreview'
import { useReduxDispatch, useReduxSelector } from 'store'
import useGroupedBasket from 'hooks/useGroupedBasket'
import useWarehouseName from 'hooks/useWarehouseName'
import ArrowBtn from 'components/buttons/ArrowBtn'
import useTranslation from 'hooks/useTranslation'
import Checkbox from 'components/inputs/Checkbox'
import { useLayoutEffect, useState } from 'react'
import BackBtn from 'components/buttons/BackBtn'
import DeliverySection from './DeliverySection'
import usePopupState from 'hooks/usePopupState'
import { useHistory } from 'react-router-dom'
import useCheckout from './hooks/useCheckout'
import SummarySection from './SummarySection'
import { useSelector } from 'react-redux'
import AddonSection from './AddonSection'
import PriceSection from './PriceSection'
import UserSection from './UserSection'
import { selectConfig } from 'appSlice'
import MagSection from './MagSection'
import './style.scss'

const OrderSummary = () => {
  const [error, setError] = useState({})
  const terms = useSelector(selectBasketTerms)
  const config = useReduxSelector(selectConfig)
  const { invoice } = useSelector(selectBasket)
  const { popup, openPopup } = usePopupState()
  const { isLoading } = useCheckout()
  const list = useGroupedBasket()

  const { push, replace } = useHistory()
  const { t } = useTranslation('basket')
  const dispatch = useReduxDispatch()
  const getName = useWarehouseName()

  useLayoutEffect(() => {
    if (list?.length === 0) replace('/map')
  }, [list?.length, replace])

  const privacyPolicyConsent = t('privacyPolicyConsent')
  const termsAndConditionsAcceptance = t('termsAndConditionsAcceptance')
  const generalRentalTermsAcceptance = t('generalRentalTermsAcceptance')
  const isWarehouseInOrder = list?.some(l => l.type === BASKET_TYPES.rent)
  const showRentalTerms = !config?.contractSigningScreenEnabled && isWarehouseInOrder

  // eslint-disable-next-line jsx-a11y/anchor-is-valid
  const Link = ({ type, children }) => <a
    className='--link'
    onClick={e => {
      e.preventDefault()
      e.stopPropagation()
      openPopup({ type: 'consent', content: type })
    }}>
    {children}
  </a>

  return (
    <>
      {popup?.type === 'consent' && <ConsentPreviewPopup
        type={popup.content}
      />}

      <div className='summary'>
        <div className='title'>
          <div>{t('summary')}</div>
          <div>{t('checkData')}</div>
        </div>

        <div className='summary__content'>
          <div className='summary__content__left'>
            <UserSection />

            {invoice && <div>
              <div className='list__header element__title'>{t('invoiceDetails')}</div>
              <div className='list__element --big'>
                <div className='element__title'>
                  <div>{invoice?.name}</div>
                  <div>NIP: {invoice?.nip}</div>
                </div>

                <div className='element__desc'>
                  <div>{invoice?.streetName}</div>
                  <div>{invoice?.postalCode} {invoice?.voivodshipName}</div>
                </div>
              </div>
            </div>}

            {list?.length > 0 && list.map((item) => item.type === BASKET_TYPES.rent
              ? <MagSection
                {...item}
                name={getName(item)}
                key={item.identifier}
              />
              : <AddonSection
                {...item}
                key={item.identifier}
              />)}

            <DeliverySection />
          </div>

          <div className='summary__content__right'>
            <PriceSection list={list} />
            <SummarySection />

            <div>
              <div className='list__header element__title'>{t('statements')}</div>
              <div className='list__element --big'>
                <div className='element__title'>
                  <span>{t('IDeclareThat')}:</span>
                </div>

                <div className='element__desc'>
                  <Checkbox
                    name='termsAndConditions'
                    checked={terms.termsAndConditions}
                    warning={error.termsAndConditions}
                    label={<>{termsAndConditionsAcceptance?.introduction} <Link type='terms'>{termsAndConditionsAcceptance?.button}</Link></>}
                    changeHandler={() => {
                      if (!terms?.termsAndConditions && error.termsAndConditions) {
                        setError(prev => ({ ...prev, termsAndConditions: false }))
                      }
                      dispatch(updateTerms({ ...terms, termsAndConditions: !terms.termsAndConditions }))
                    }}
                  />
                  <Checkbox
                    name='privacyPolicy'
                    checked={terms.privacyPolicy}
                    warning={error.privacyPolicy}
                    label={<>{privacyPolicyConsent?.introduction} <Link type='privacy'>{privacyPolicyConsent?.button}</Link></>}
                    changeHandler={() => {
                      if (!terms?.privacyPolicy && error.privacyPolicy) {
                        setError(prev => ({ ...prev, privacyPolicy: false }))
                      }
                      dispatch(updateTerms({ ...terms, privacyPolicy: !terms.privacyPolicy }))
                    }}
                  />
                  {showRentalTerms && <Checkbox
                    name='rentalTerms'
                    checked={terms.rentalTerms}
                    warning={error.rentalTerms}
                    label={<>{generalRentalTermsAcceptance?.introduction} {generalRentalTermsAcceptance?.button} {generalRentalTermsAcceptance?.conclusion}</>}
                    changeHandler={() => {
                      if (!terms?.rentalTerms && error.rentalTerms) {
                        setError(prev => ({ ...prev, rentalTerms: false }))
                      }
                      dispatch(updateTerms({ ...terms, rentalTerms: !terms.rentalTerms }))
                    }}
                  />}
                </div>
              </div>
            </div>

            <div className='summary__buttons'>
              <ArrowBtn
                enlarged
                text={t(config?.contractSigningScreenEnabled ? 'signTheContract' : 'pay')}
                isLoading={isLoading}
                action={() => {
                  if (isLoading) return
                  if (terms.privacyPolicy && terms.termsAndConditions && (showRentalTerms ? terms.rentalTerms : true)) {
                    window.scrollTo({ top: 0 })
                    push(config?.contractSigningScreenEnabled
                      ? '/order/contract'
                      : '/order/payment')
                  } else {
                    setError({
                      privacyPolicy: !terms.privacyPolicy,
                      termsAndConditions: !terms.termsAndConditions,
                      rentalTerms: showRentalTerms ? !terms.rentalTerms : false
                    })
                  }
                }} />
              <BackBtn />
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default OrderSummary